import moment from 'moment';
import { Logger } from '@/helpers/log';
import { naturalSort, reverseNaturalSort } from '@/helpers/util';
import * as Convert from '@/mixins/convert';
import 'moment-timezone';
import { DisplayTypes, SxsDisplayTypes, aggregateOps } from './constants';
import { getBlankKpi, getFullOp, kpiToSaveObject, saveObjectToKpi } from './data';
import { formatChart, formatGroupedSxs, formatMultipleMetricsChart, formatRelativeStackedTimeChart, formatRelativeTimeChart, formatSingleResult, formatStackedTimeseries, formatSxsDuration, formatTimeseries } from './formatters';

const logger = new Logger('kpi:kpi');

export default class Kpi {
  constructor(kpi, {
    filters,
    properties,
    globalFilter,
    time,
    model,
  }) {
    Object.assign(this, getBlankKpi(), kpi, {
      filters,
      properties,
      listableProperties: properties,
      globalFilter,
    });
    if (time) {
      this.time = time;
    }

    if (model && model.pivot) {
      this.pivot = model.pivot;
    }

    if (this.boundaries) {
      this.displayOptions.boundaries = this.boundaries;
      this.displayOptions.positiveStatus = this.boundariesReversed || false;
    }
  }

  toSaveObject() {
    return kpiToSaveObject(this);
  }

  static fromSaveObject(kpi, filters, properties) {
    return new Kpi(saveObjectToKpi(kpi, filters, properties), { filters, properties });
  }

  clone() {
    return Object.assign(new Kpi(this, {
      filters: this.filters,
      properties: this.properties,
    }), this);
  }

  // Add convert mixins
  convertFilterIdToFilter(filterId) {
    return Convert.convertFilterIdToFilter(filterId, this);
  }

  convertPropertyNameToProperty(propertyName) {
    return Convert.convertPropertyNameToProperty(propertyName, this);
  }

  /**
   * Get the queries that need to be run to get the data for this KPI
   * @returns {Object}
   */
  getQueries() {
    const now = Date.now();
    let query = {
      start: now - this.time.offset,
      end: now,
    };
    let relevanceQuery = null;

    const {
      grouping,
      scope,
      dragBins,
      binSize,
      options,
      globalFilter,
      timezoneProperty,
      custom,
      sxs,
    } = this;

    // @TODO handle more than one measurement
    const [measurement] = this.measurements;
    if (measurement.op === 'percentOfTimeMatching' && measurement.property) {
      this.grouping = measurement.property;
    }

    query.op = this.getOperator();

    query.timeseries = this.isTimeseries();
    query.dragBins = dragBins || false;
    if (query.timeseries && binSize) {
      query.timezone = moment.tz.guess();

      // Handle month mode
      if (binSize === '1M') {
        const startOfMonth = moment(query.start).startOf('month');
        query.start = startOfMonth.valueOf();
      }

      if (this.isRelativeTimeseries()) {
        query.relativeTime = binSize;
        // extend start to envelope a full set of complete bins
        // then rely on incomplete bin removal to select those only
        // take off one minute to prevent edge bin being counted twice on exact boundary.
        query.withIncompleteBins = false;
        let relativeOffset = 0;
        if (binSize === 'hours') {
          relativeOffset = 1000 * ((60 * 60) - 60);
        } else if (binSize === 'days') {
          relativeOffset = 1000 * ((60 * 60 * 24) - 60);
        } else {
          logger.error('unable to offset relative time query', relativeOffset);
        }
        query.start -= relativeOffset;

        // Add the timezone property if present
        query.timezoneFilterStr = timezoneProperty || 'timezone';
      } else if (binSize !== 'auto') {
        query.binSize = binSize;
      }
    }

    // Build the valueFilterStr
    if (measurement.expression) {
      query.valueFilterStr = measurement.expression;
    } else if (this.isFilterMeasurement(measurement)) {
      if (!measurement.filter) {
        return { kpiQuery: null };
      }
      const f = this.convertFilterIdToFilter(measurement.filter);
      query.valueFilterStr = f.expression;
    } else if (this.isPropertyMeasurement(measurement)) {
      if (!measurement.property) {
        return { kpiQuery: null };
      }
      const p = this.convertPropertyNameToProperty(measurement.property);
      query.valueFilterStr = p.field;
    }

    // Add in any global filters
    if (globalFilter && globalFilter.expression) {
      query.select = {
        scopeFilterStr: `(${globalFilter.expression})`,
      };
    }

    // Add scoping, if defined
    if (scope) {
      const s = this.convertFilterIdToFilter(scope);
      if (globalFilter && globalFilter.expression) {
        query.select.scopeFilterStr += ` && (${s.expression})`;
      } else {
        query.select = {
          scopeFilterStr: s.expression,
        };
      }
    }

    // Add grouping, if defined
    if (grouping && !this.isTimeseries()) {
      query.keyFilterStr = this.convertPropertyNameToProperty(grouping).field;

      relevanceQuery = JSON.parse(JSON.stringify(query));
      relevanceQuery.op = 'countDevices';
      relevanceQuery.valueFilterStr = 'true';

      // Queries by devices should always use incomplete bins
      if (query.keyFilterStr === '$id') {
        query.withIncompleteBins = true;
      }
    } else if (grouping) {
      query.keyFilterStr = this.convertPropertyNameToProperty(grouping).field;
    } else if (!this.isTimeseries()) {
      relevanceQuery = JSON.parse(JSON.stringify(query));
      relevanceQuery.op = 'countDevices';
      relevanceQuery.valueFilterStr = 'true';
    }

    // Add limiting, if defined
    if (
      grouping // Only allow limiting if grouped
      && aggregateOps.includes(query.op)
      && options && options.limitOrder
      && (options.limitOrder === 'top' || options.limitOrder === 'bottom')
      && Number.parseInt(options.limitCount, 10) > 0
    ) {
      query.selector = {
        op: options.limitOrder,
        length: Number.parseInt(options.limitCount, 10),
      };
    }

    // Parse and merge and custom query stuff
    if (custom && this.isValidCustom()) {
      const customQuery = this.parsedCustom();
      query = {
        ...query,
        ...customQuery,
      };

      if (customQuery.view) {
        query.valueFilterStr = '$result';
      }

      if (relevanceQuery) {
        relevanceQuery = {
          ...relevanceQuery,
          ...customQuery,
        };

        if (customQuery.view) {
          relevanceQuery.valueFilterStr = '$result';
        }
      }

      Object.keys(customQuery).forEach((key) => {
        if (customQuery[key] === null) {
          delete query[key];
          if (relevanceQuery) {
            delete relevanceQuery[key];
          }
        }
      });
    }

    if (this.isSxs()) {
      query.sxs = {
        duration: sxs.duration,
        type: 'duration',
        join: 'group',
      };
      query.dragBins = true;
    } else {
      delete query.sxs;
    }

    return { kpiQuery: query, relevanceQuery };
  }

  getOperatorObj(measurement) {
    const m = measurement || this.measurements[0];
    return getFullOp(m.op);
  }

  getOperator() {
    if (this.custom && this.isValidCustom()) {
      const parsed = this.parsedCustom();
      if (parsed !== null && parsed?.op) {
        return getFullOp(parsed?.op) || parsed.op;
      }
    }

    // @TODO handle more than one measurement
    const [measurement] = this.measurements;
    const fullOp = getFullOp(measurement.op);
    if (fullOp?.op) {
      return fullOp.op;
    }
    return measurement.op;
  }

  /* eslint-disable class-methods-use-this */
  getMeasurementType(measurement) {
    const { op } = measurement;
    const { typeOfValue } = getFullOp(op) || {};
    return typeOfValue;
  }
  /* eslint-enable class-methods-use-this */

  isPropertyMeasurement(measurement) {
    return !this.custom && this.getMeasurementType(measurement) === 'property';
  }

  isFilterMeasurement(measurement) {
    return !this.custom && this.getMeasurementType(measurement) === 'filter';
  }

  isExpressionMeasurement(measurement) {
    return !this.custom && this.getMeasurementType(measurement) === 'expression';
  }

  getScope() {
    return this.scope;
  }

  /**
   * Sort the data using the displayOptions specified on the KPI
   * @param {Array} data The data array that needs to be sorted
   */
  sortData(data) {
    const sort = this.getSort();

    let sortedData = [];
    const key = (sort.by === 'metric') ? 'y' : 'x';

    if (sort.order === 'asc') {
      sortedData = naturalSort(data, key);
    } else {
      sortedData = reverseNaturalSort(data, key);
    }
    return sortedData;
  }

  /**
   * Format the data based on the type of KPI and return the information on how to display the
   * results, i.e. chart or indicator
   *
   * @param {*} resultData
   * @param {*} relevanceData
   */
  getFormattedResults(resultData, relevanceData) {
    const displayData = {};
    const displayType = null;
    let formatFunc = null;

    if (!resultData) {
      return { displayType, displayData, isPercent: this.isPercent() };
    }

    if (this.hasMultipleMeasurements()) {
      // Bypass formatting if it's tabular
      if (this.getDisplayType() === DisplayTypes.TABULAR) {
        return {
          displayType: DisplayTypes.TABULAR,
          displayData: resultData,
          isPercent: this.isPercent(),
        };
      }
      return formatMultipleMetricsChart(this, resultData, relevanceData);
    }

    // Single metrics
    if (this.isRelativeTimeseries() && this.isGrouped()) {
      formatFunc = formatRelativeStackedTimeChart;
    } else if (this.isRelativeTimeseries()) {
      formatFunc = formatRelativeTimeChart;
    } else if (this.isTimeseries() && this.isGrouped()) {
      formatFunc = formatStackedTimeseries;
    } else if (this.isSxs() && this.isGrouped()) {
      formatFunc = formatGroupedSxs;
    } else if (this.isSxs()) {
      formatFunc = formatSxsDuration;
    } else if (this.isTimeseries()) {
      formatFunc = formatTimeseries;
    } else if (this.isGrouped()) {
      formatFunc = formatChart;
    }

    if (!formatFunc) {
      formatFunc = formatSingleResult;
    }

    return formatFunc(this, resultData, relevanceData);
  }

  hasMultipleMeasurements() {
    return this.measurements && this.measurements.length > 1;
  }

  allMeasurementsMatch(filterFunc = () => true) {
    const { measurements } = this;
    if (!measurements || measurements.length === 0) return false;
    return measurements.every(filterFunc);
  }

  anyMeasurementsMatch(filterFunc = () => true) {
    const { measurements } = this;
    if (!measurements || measurements.length === 0) return false;
    return measurements.some(filterFunc);
  }

  noMeasurementsMatch(filterFunc = () => true) {
    const { measurements } = this;
    if (!measurements || measurements.length === 0) return false;
    return !measurements.some(filterFunc);
  }

  getFirstMatchingMeasurement(filterFunc = () => true) {
    const { measurements } = this;
    if (!measurements || measurements.length === 0) return null;
    return measurements.find(filterFunc) || null;
  }

  getAllMatchingMeasurement(filterFunc = () => true) {
    const { measurements } = this;
    if (!measurements || measurements.length === 0) return null;
    return measurements.filter(filterFunc) || null;
  }

  definesMeasurement() {
    const { measurements } = this;
    if (!measurements || measurements.length === 0) return false;

    return this.anyMeasurementsMatch((measurement) => {
      const {
        op,
        filter,
        property,
        expression,
      } = measurement;
      return (op !== null && op !== '')
        && (
          ((filter && this.isFilterMeasurement(measurement)) !== null)
          || ((property && this.isPropertyMeasurement(measurement)) !== null)
          || !!expression
        );
    });
  }

  definesMonitor() {
    const { monitor: { threshold: { op, value }, periodSize } } = this;
    return (op && value && periodSize) !== null;
  }

  parsedCustom() {
    if (!this.custom) {
      return null;
    }
    try {
      const parsed = JSON.parse(this.custom);
      return parsed;
    } catch (e) {
      return null;
    }
  }

  isValidCustom() {
    if (!this.custom) {
      return false;
    }
    return this.parsedCustom() !== null;
  }

  isValid() {
    if (this.custom && this.isValidCustom()) {
      return true;
    }
    return this.definesMeasurement() && (!this.isMonitored() || this.definesMonitor());
  }

  isPercentOperation() {
    const percentOps = [
      'duration',
      'percentOfTimeMatching',
      'countPercentDevices',
    ];
    return this.anyMeasurementsMatch(({ op }) => percentOps.includes(op));
  }

  /**
   * Returns true if the result of the KPI is represented as a percentage
   * @returns {Boolean}
   */
  isPercent() {
    if (this.isSxs() && this.getSxsDisplayType().endsWith('percent')) {
      return true;
    }
    return this.isPercentOperation();
  }

  /**
   * Returns true if the KPI is percent based, but also a property value
   * @returns {Boolean}
   */
  isPropertyPercent() {
    const percentOps = ['percentOfTimeMatching'];
    return this.anyMeasurementsMatch((m) => percentOps.includes(m.op));
  }

  isPropertyCount() {
    const countOps = ['countDistinct', 'count'];
    return this.anyMeasurementsMatch((m) => countOps.includes(m.op));
  }

  isCalculatedPercent() {
    if (this.isSxs()) {
      return this.getSxsDisplayType().endsWith('percent')
        || (this.isPercent() && this.getSxsDisplayType().startsWith('delta'));
    }
    return false;
  }

  /**
   * Returns true if the results of the KPI can be added together for a meaningful result
   * @returns {Boolean}
   */
  isSummable() {
    if (
      (this.isTimeseries() && !this.isGrouped()) // Catch the case of a timeseries with no grouping
      || this.isSxs() // SxS are independent, so never sum.
    ) {
      return false;
    }
    const summableOps = ['countDevices', 'countDistinct', 'count', 'sum'];
    return this.anyMeasurementsMatch((m) => summableOps.includes(m.op));
  }

  /**
   * Returns true if the X axis is a timeseries
   * @returns {Boolean}
   */
  isTimeseries() {
    const { binSize } = this;
    return binSize !== null;
  }

  isRelativeTimeseries() {
    const { binSize } = this;
    const relativeBins = ['hours', 'days'];
    return relativeBins.includes(binSize);
  }

  isMonitored() {
    const { monitored } = this;
    return monitored === true;
  }

  isSxs() {
    const { sxs } = this;
    return sxs && this.canSxs() && sxs.numberOfUnits && sxs.unit;
  }

  canBeGrouped() {
    return !this.isMonitored();
  }

  canBeGroupedByProperty() {
    return this.canBeGrouped()
      && this.anyMeasurementsMatch(({ op }) => op !== 'percentOfTimeMatching');
  }

  /**
   * Returns true if the KPI has a grouping set
   */
  isGrouped() {
    const { grouping } = this;
    return grouping !== null;
  }

  /**
   * Returns true if the results can be sorted on either axis
   * @returns {Boolean}
   */
  isSortable() {
    if (this.isRelativeTimeseries() && this.isGrouped()) {
      return true;
    } if (this.isTimeseries() && !this.isGrouped()) {
      return false;
    } if (this.isTimeseries() && this.isGrouped()) {
      return true;
    } if (this.isGrouped()) {
      return true;
    }
    return false;
  }

  canSxs() {
    // Only allow either timeseries or grouped, but not both
    const isTimeseriesOrGrouped = this.isTimeseries() ^ this.isGrouped(); // eslint-disable-line max-len, no-bitwise
    return isTimeseriesOrGrouped && !this.isRelativeTimeseries() && !this.isPropertyPercent();
  }

  isStacked() {
    return this.isTimeseries() && (this.isGrouped() || this.isSxs());
  }

  canBeLimited() {
    const { grouping, binSize } = this;
    if (!grouping || binSize || this.isSxs()) {
      return false;
    }
    return this.allMeasurementsMatch((m) => aggregateOps.includes(m.op));
  }

  isNoGaps() {
    const { displayOptions: { hideBarGaps } } = this;
    return hideBarGaps;
  }

  isChart() {
    return this.isTimeseries() || this.isGrouped();
  }

  getPropertyValueKey(prop, val, isXAxis) {
    if (!val) return val;
    if (this.isPropertyCount() && !isXAxis) return val;

    const fullProp = this.convertPropertyNameToProperty(prop);
    if (!fullProp) return val;

    if (!isXAxis) {
      if (!fullProp.factor || fullProp.factor === 1) return val;
      return val * fullProp.factor;
    }
    return fullProp.format(val);
  }

  /**
   * Get the displayType set on the KPI, or falls back to a sensible default
   * @returns {String}
   */
  getDisplayType() {
    let defaultDisplayType = 'single';

    if (this.isTimeseries() || this.isGrouped()) {
      defaultDisplayType = 'bar';
    }
    const { displayOptions: { type } } = this;
    return type || defaultDisplayType;
  }

  getSxsDisplayType() {
    const defaultDisplayType = SxsDisplayTypes.ABSOLUTE;
    const { displayOptions: { sxsType } } = this;
    return sxsType || defaultDisplayType;
  }

  /**
   * Get the Plotly trace "type" field for each DisplayType
   * @returns {String}
   */
  getChartDisplayType() {
    switch (this.getDisplayType()) {
      case DisplayTypes.BAR: return 'bar';
      case DisplayTypes.LINE: return 'line';
      case DisplayTypes.GROUPED_BAR: return 'bar';
      case DisplayTypes.STACKED_BAR: return 'bar';
      case DisplayTypes.PIE: return 'pie';
      default: return null;
    }
  }

  /**
   * Get an array of DisplayTypes that are compatible with the current KPI config
   * @returns {Array}
   */
  getPossibleDisplayTypes() {
    const defaultTypes = [DisplayTypes.TABULAR];

    if (this.hasMultipleMeasurements()) {
      if (this.isTimeseries() && this.isGrouped()) {
        return defaultTypes;
      }
      if (this.isTimeseries() || this.isGrouped()) {
        return defaultTypes.concat([
          DisplayTypes.LINE,
          DisplayTypes.GROUPED_BAR,
        ]);
      }
      return defaultTypes;
    }

    if (this.isTimeseries()) {
      if (this.isSxs()) {
        const bar = this.getSxsDisplayType() === SxsDisplayTypes.ABSOLUTE
          ? DisplayTypes.GROUPED_BAR
          : DisplayTypes.BAR;
        return defaultTypes.concat([DisplayTypes.LINE, bar]);
      }
      if (this.isPercent() && this.isGrouped()) {
        // Grouped timeseries (with % query)
        if (this.isPropertyPercent()) {
          return defaultTypes.concat([
            DisplayTypes.LINE,
            DisplayTypes.GROUPED_BAR,
            DisplayTypes.STACKED_BAR,
          ]);
        }
        return defaultTypes.concat([DisplayTypes.LINE, DisplayTypes.GROUPED_BAR]);
      } if (this.isGrouped() && !this.isSummable()) {
        // Grouped timeseries
        return defaultTypes.concat([
          DisplayTypes.LINE,
          DisplayTypes.GROUPED_BAR,
          DisplayTypes.STACKED_BAR,
        ]);
      } if (this.isPercent()) {
        // Timeseries % query
        return defaultTypes.concat([DisplayTypes.BAR, DisplayTypes.LINE]);
      } if (this.isSummable()) {
        // Timeseries summable
        return defaultTypes.concat([
          DisplayTypes.LINE,
          DisplayTypes.GROUPED_BAR,
          DisplayTypes.STACKED_BAR,
          // DisplayTypes.PIE,
        ]);
      }
      // Timeseries
      return defaultTypes.concat([
        DisplayTypes.BAR,
        DisplayTypes.LINE,
        DisplayTypes.SCATTER,
      ]);
    }

    if (this.isGrouped()) {
      // Grouped query
      if (this.isPercent() || this.isSummable()) {
        return defaultTypes.concat([
          DisplayTypes.BAR,
          DisplayTypes.LINE,
          DisplayTypes.SCATTER,
          DisplayTypes.PIE,
        ]);
      }
      return defaultTypes.concat([DisplayTypes.BAR, DisplayTypes.LINE, DisplayTypes.SCATTER]);
    }

    // Single value
    return defaultTypes.concat([DisplayTypes.SINGLE, DisplayTypes.GAUGE]);
  }

  /**
   * Get the sorting for the results in the format:
   * { by: 'metric', order: 'desc' }
   *
   * @returns {Object}
   */
  getSort() {
    const { displayOptions: { sort } } = this;
    return sort;
  }

  getYAxis() {
    const { displayOptions: { yAxis } } = this;
    return yAxis;
  }

  getValueBounds() {
    const { displayOptions: { valueBounds } } = this;
    return valueBounds;
  }

  /**
   * Get the saved state of the KPI
   */
  getState() {
    const { state } = this;
    return state;
  }

  /**
   * Get the name of the KPI
   */
  getName() {
    const { name } = this;
    return name;
  }

  getBoundaries() {
    if (this.isSxs()) {
      return null;
    }
    const { displayOptions: { boundaries } } = this;
    return boundaries;
  }

  getTitles() {
    const { displayOptions: { titles: { x = null, y = null } } } = this;
    return { x, y };
  }

  getUnits() {
    const { displayOptions: { units = null } } = this;
    return units;
  }

  isPositiveStatus() {
    const { displayOptions: { positiveStatus } } = this;
    return positiveStatus;
  }

  /**
   * Get the property name for the metric, if this is a property-based KPI
   */
  getMetricPropertyName(measurement) {
    const { property } = measurement;
    if (this.isPropertyMeasurement(measurement)) {
      const fullProp = this.convertPropertyNameToProperty(property);
      if (fullProp) {
        return fullProp.alias;
      }
    }
    return null;
  }

  /**
   * Set the displayOptions key of the current KPI, useful for adding additional config
   * @param {Object} displayOptions Object containing display config
   */
  setDisplayOptions(displayOptions) {
    this.displayOptions = displayOptions;
  }

  /**
   * Set the KPI period that is used to construct the query
   * @param {Object} period A valid time period object with a key of "offset"
   */
  setTime(period) {
    this.time = period;
  }

  setBinSize(binSize) {
    this.binSize = binSize;
  }

  setDragBins(dragBins) {
    this.dragBins = dragBins;
  }

  setGrouping(grouping) {
    this.grouping = grouping;
  }

  setScope(scope) {
    this.scope = scope;
  }

  setGlobalFilter(filter) {
    this.globalFilter = filter;
  }

  setBoundaries(boundaries = {}, isPositive = false) {
    this.displayOptions.boundaries = boundaries;
    this.displayOptions.positiveStatus = isPositive || false;
  }

  isUnderConstruction() {
    return this.underConstruction;
  }

  setUnderConstruction() {
    this.underConstruction = true;
  }
}
